// /client/src/components/Programs.js
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const Programs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOffering, setSelectedOffering] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  
  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'descending'
  });

  // Fetch programs from API
  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      const response = await fetch('/api/programs', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs || []);
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        setError(errorData.error || 'Failed to fetch programs');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch programs on component mount
  useEffect(() => {
    fetchPrograms();
  }, []);

  // Check for success message from location state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedPrograms = useMemo(() => {
    let sortablePrograms = [...programs];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      sortablePrograms = sortablePrograms.filter(program => {
        const programName = program.programName?.toLowerCase() || '';
        const productName = program.productName?.toLowerCase() || '';
        return programName.includes(searchLower) || productName.includes(searchLower);
      });
    }
    
    // Apply offering filter
    if (selectedOffering) {
      sortablePrograms = sortablePrograms.filter(program => program.offering === selectedOffering);
    }
    
    // Apply status filter
    if (selectedStatus) {
      sortablePrograms = sortablePrograms.filter(program => program.status === selectedStatus);
    }
    
    // Apply sorting
    if (sortConfig !== null) {
      sortablePrograms.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'createdAt') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (sortConfig.key === 'timeframe') {
          // Use productDays from the product
          aValue = parseInt(a.productDays || 0);
          bValue = parseInt(b.productDays || 0);
        } else {
          aValue = aValue ? aValue.toString().toLowerCase() : '';
          bValue = bValue ? bValue.toString().toLowerCase() : '';
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortablePrograms;
  }, [programs, searchTerm, selectedOffering, selectedStatus, sortConfig]);

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  const handleRowClick = (programId) => {
    // Navigate to edit program page
    navigate(`/programs/edit/${programId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreateProgram = () => {
    navigate('/programs/create');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'is-success';
      case 'archived':
        return 'is-warning';
      default:
        return 'is-light';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedOffering('');
    setSelectedStatus('');
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="has-text-centered py-6">
                <div className="is-loading"></div>
                <p>Loading programs...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between'>
              <h1 className='h1 my-2'>Programs</h1>
              <div>
                <button 
                  className="button"
                  onClick={handleCreateProgram}
                >
                  Create New Program
                </button>
              </div>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
                <br />
                <button 
                  className="button is-small is-light mt-2" 
                  onClick={fetchPrograms}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Search and Filter Section */}
            <div className="box mb-4">
              <div className="columns">
                <div className="column is-5">
                  <div className="field">
                    <label className="pt-0 label">Search</label>
                    <div className="control has-icons-left">
                      <input
                        className="input"
                        type="text"
                        placeholder="Search by program or product name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <span className="icon is-small is-left">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="m21 21-4.35-4.35"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">Filter by Offering</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedOffering}
                          onChange={(e) => setSelectedOffering(e.target.value)}
                        >
                          <option value="">All Offerings</option>
                          <option value="Standard">Standard</option>
                          <option value="Nuanced">Nuanced</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">Filter by Status</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-1">
                  <div className="field">
                    <label className="pt-0 label">&nbsp;</label>
                    <div className="control">
                      <button 
                        className="button is-light is-fullwidth"
                        onClick={clearFilters}
                        disabled={!searchTerm && !selectedOffering && !selectedStatus}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {sortedPrograms.length === 0 && !loading && !error ? (
              <div className="notification is-info is-light">
                <p>
                  {searchTerm || selectedOffering || selectedStatus
                    ? 'No programs match your search criteria.'
                    : 'No programs created yet.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 is-flex is-justify-content-space-between is-align-items-center">
                  <p className="is-size-7 has-text-grey">
                    Showing {sortedPrograms.length} of {programs.length} program{programs.length !== 1 ? 's' : ''}
                  </p>
                  {(searchTerm || selectedOffering || selectedStatus) && (
                    <div className="tags">
                      {searchTerm && (
                        <span className="tag is-info">
                          Search: "{searchTerm}"
                          <button 
                            className="delete is-small" 
                            onClick={() => setSearchTerm('')}
                          ></button>
                        </span>
                      )}
                      {selectedOffering && (
                        <span className="tag is-info">
                          Offering: {selectedOffering}
                          <button 
                            className="delete is-small" 
                            onClick={() => setSelectedOffering('')}
                          ></button>
                        </span>
                      )}
                      {selectedStatus && (
                        <span className="tag is-info">
                          Status: {selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}
                          <button 
                            className="delete is-small" 
                            onClick={() => setSelectedStatus('')}
                          ></button>
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th 
                          onClick={() => handleSort('programName')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'programName' ? 'colorBlack' : 'colorBlue'}
                        >
                          Program Name {getSortIcon('programName')}
                        </th>
                        <th 
                          onClick={() => handleSort('productName')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'productName' ? 'colorBlack' : 'colorBlue'}
                        >
                          Product Name {getSortIcon('productName')}
                        </th>
                        <th 
                          onClick={() => handleSort('timeframe')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'timeframe' ? 'colorBlack' : 'colorBlue'}
                        >
                          Timeframe (Messages) {getSortIcon('timeframe')}
                        </th>
                        <th 
                          onClick={() => handleSort('offering')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'offering' ? 'colorBlack' : 'colorBlue'}
                        >
                          Offering {getSortIcon('offering')}
                        </th>
                        <th 
                          onClick={() => handleSort('status')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'status' ? 'colorBlack' : 'colorBlue'}
                        >
                          Status {getSortIcon('status')}
                        </th>
                        <th 
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'createdAt' ? 'colorBlack' : 'colorBlue'}
                        >
                          Created Date {getSortIcon('createdAt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPrograms.map((program) => (
                        <tr 
                          key={program.id || program._id}
                          onClick={() => handleRowClick(program.id || program._id)}
                          style={{
                            cursor: 'pointer',
                            transition: 'transform 0.1s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <td>{program.programName}</td>
                          <td>{program.productName || 'N/A'}</td>
                          <td>{program.productDays || 'N/A'}</td>
                          <td>
                            <span className={`tag ${
                              program.offering === 'Premium' ? 'is-success' :
                              program.offering === 'Nuanced' ? 'backgroundColorCyan' :
                              program.offering === 'Standard' ? 'backgroundColorGreen' :
                              'backgroundColorGrey'
                            }`}>
                              {program.offering}
                            </span>
                          </td>
                          <td>
                            <span className={`tag ${getStatusColor(program.status)}`}>
                              {program.status ? program.status.charAt(0).toUpperCase() + program.status.slice(1) : 'N/A'}
                            </span>
                          </td>
                          <td>{program.createdAt ? formatDate(program.createdAt) : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          </div>

        </div>
      </section>
    </>
  );
};

export default Programs;